/* This FAQ component is specifically for language/accent pages */
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import MarkdownBlock from './Chat/MarkdownBlock';

import { LanguageOption } from './TextToSpeechLanguages';

interface TTSLanguageFaqProps {
  languageObj: LanguageOption;
}

export default function TTSLanguageFaq({ languageObj }: TTSLanguageFaqProps) {
  const { t } = useLocale('text-to-speech');

  // Generate language-specific FAQs
  const generateLanguageFaqs = () => {
    const isAccent = languageObj.isAccent;
    const languageName = languageObj.name;

    // Voice style pages (funny, scary, ...) get their own FAQ set —
    // the language/accent questions don't make sense for a style.
    if (languageObj.isStyle) {
      return [
        {
          question: t('How does the {{language}} voice generator work?', {
            language: t(languageName),
          }),
          answer: t(
            'Type or paste your script, pick one of our {{language}} AI voices, and click Generate. The AI renders your text as natural-sounding speech in that style within seconds, and you can download the result as an MP3 file.',
            { language: t(languageName) },
          ),
        },
        {
          question: t(
            'Can I use the {{language}} voices for commercial projects?',
            { language: t(languageName) },
          ),
          answer: t(
            "Yes, you can use our {{language}} text-to-speech voices for commercial projects. Once you generate the audio, you own the rights to use it for any purpose, including commercial use, provided you have the necessary rights to the content you're converting and the content complies with applicable laws.",
            { language: t(languageName) },
          ),
        },
        {
          question: t(
            'What can I create with {{language}} text to speech voices?',
            { language: t(languageName) },
          ),
          answer: t(
            '{{language}} voices are perfect for YouTube videos, TikToks, memes, game characters, podcasts, voicemail greetings, pranks between friends, and any content that needs a distinctive vocal personality. Combine them with our voice cloning and audio tools for even more creative options.',
            { language: t(languageName) },
          ),
        },
        {
          question: t(
            'Can I adjust the speed and tone of {{language}} voices?',
            { language: t(languageName) },
          ),
          answer: t(
            'Yes, our {{language}} text-to-speech tool allows you to customize various aspects of the generated speech. You can adjust the speaking speed, modify the tone and emotion, and fine-tune other voice characteristics to match your specific needs and preferences.',
            { language: t(languageName) },
          ),
        },
      ];
    }

    const baseFaqs = [
      {
        question: isAccent
          ? t('How accurate is the {{language}} accent text-to-speech?', {
              language: t(languageName),
            })
          : t('How accurate is the {{language}} text-to-speech?', {
              language: t(languageName),
            }),
        answer: isAccent
          ? t(
              'Our {{language}} accent text-to-speech technology uses advanced AI models trained specifically on {{language}} accent patterns and speech characteristics. The AI captures the unique pronunciation, intonation, and rhythm that makes the {{language}} accent distinctive, delivering highly accurate and natural-sounding speech synthesis.',
              { language: t(languageName) },
            )
          : t(
              'Our {{language}} text-to-speech technology uses advanced AI models trained specifically on {{language}} language patterns and speech characteristics. The AI understands {{language}} grammar, pronunciation rules, and cultural nuances, delivering highly accurate and natural-sounding speech synthesis.',
              { language: t(languageName) },
            ),
      },
      {
        question: isAccent
          ? t('Can I use {{language}} accent voices for commercial projects?', {
              language: t(languageName),
            })
          : t('Can I use {{language}} voices for commercial projects?', {
              language: t(languageName),
            }),
        answer: isAccent
          ? t(
              "Yes, you can use our {{language}} accent text-to-speech voices for commercial projects. Once you generate the audio, you own the rights to use it for any purpose, including commercial use, provided you have the necessary rights to the content you're converting and the content complies with applicable laws.",
              { language: t(languageName) },
            )
          : t(
              "Yes, you can use our {{language}} text-to-speech voices for commercial projects. Once you generate the audio, you own the rights to use it for any purpose, including commercial use, provided you have the necessary rights to the content you're converting and the content complies with applicable laws.",
              { language: t(languageName) },
            ),
      },
      {
        question: isAccent
          ? t(
              'What makes {{language}} accent different from other English accents?',
              { language: t(languageName) },
            )
          : t(
              'What file formats are supported for {{language}} text-to-speech?',
              { language: t(languageName) },
            ),
        answer: isAccent
          ? t(
              'The {{language}} accent has distinctive features including unique vowel sounds, specific consonant pronunciations, rhythm patterns, and intonation characteristics. Our AI model has been trained to capture these nuances, including regional variations within the {{language}} accent, ensuring authentic and recognizable speech output.',
              { language: t(languageName) },
            )
          : t(
              'Our {{language}} text-to-speech service generates high-quality MP3 audio files by default. These files are compatible with all major platforms and devices, making them perfect for podcasts, videos, audiobooks, presentations, and any other audio content you need.',
              { language: t(languageName) },
            ),
      },
      {
        question: isAccent
          ? t(
              'Can I adjust the speed and tone of {{language}} accent voices?',
              { language: t(languageName) },
            )
          : t('Can I adjust the speed and tone of {{language}} voices?', {
              language: t(languageName),
            }),
        answer: isAccent
          ? t(
              'Yes, our {{language}} accent text-to-speech tool allows you to customize various aspects of the generated speech. You can adjust the speaking speed, modify the tone and emotion, and fine-tune other voice characteristics to match your specific needs and preferences.',
              { language: t(languageName) },
            )
          : t(
              'Yes, our {{language}} text-to-speech tool allows you to customize various aspects of the generated speech. You can adjust the speaking speed, modify the tone and emotion, and fine-tune other voice characteristics to match your specific needs and preferences.',
              { language: t(languageName) },
            ),
      },
    ];

    // Add language-specific FAQ if not an accent
    if (!isAccent) {
      baseFaqs.push({
        question: t(
          'Does the {{language}} text-to-speech support different regional variations?',
          { language: t(languageName) },
        ),
        answer: t(
          'Yes, our {{language}} text-to-speech technology recognizes and supports various regional dialects and variations within the {{language}} language. The AI model adapts to different pronunciation patterns and regional characteristics to provide authentic-sounding speech for different {{language}}-speaking regions.',
          { language: t(languageName) },
        ),
      });
    }

    // Add accent-specific FAQ
    if (isAccent) {
      baseFaqs.push({
        question: t(
          'Is the {{language}} accent suitable for professional voice-overs?',
          { language: t(languageName) },
        ),
        answer: t(
          "Absolutely! Our {{language}} accent text-to-speech produces professional-quality audio that's perfect for voice-overs, commercials, documentaries, educational content, and corporate presentations. The natural intonation and authentic accent characteristics make it ideal for professional applications.",
          { language: t(languageName) },
        ),
      });
    }

    return baseFaqs;
  };

  const faqs = generateLanguageFaqs();

  return (
    <div className='bg-gray-50 dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl py-12 px-4 sm:py-16 sm:px-6 lg:px-8'>
        <div
          className='mx-auto max-w-3xl'
          itemScope
          itemType='https://schema.org/FAQPage'
        >
          <h2 className='text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-slate-200'>
            {t('Frequently asked questions')}
          </h2>
          <dl className='mt-8 space-y-4'>
            {faqs.map((faq, index) => (
              <Disclosure
                as='div'
                key={index}
                className='rounded-2xl bg-white dark:bg-slate-800/80 p-6 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10 transition-all duration-300'
                itemScope
                itemProp='mainEntity'
                itemType='https://schema.org/Question'
              >
                {({ open }) => (
                  <>
                    <dt className='text-lg'>
                      <Disclosure.Button className='flex w-full items-start justify-between text-left text-gray-400 dark:text-slate-400'>
                        <span
                          className='font-medium text-gray-900 dark:text-slate-200'
                          itemProp='name'
                        >
                          {faq.question}
                        </span>
                        <span className='ml-6 flex h-7 items-center'>
                          <ChevronDownIcon
                            className={clsxm(
                              open ? '-rotate-180' : 'rotate-0',
                              'h-6 w-6 transform',
                            )}
                            aria-hidden='true'
                          />
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel
                      as='dd'
                      className='mt-4 pr-12'
                      itemScope
                      itemProp='acceptedAnswer'
                      unmount={false}
                      itemType='https://schema.org/Answer'
                    >
                      <div
                        className='text-base text-gray-500 dark:text-slate-400'
                        itemProp='text'
                      >
                        <MarkdownBlock
                          message={faq.answer.replace(/\n/g, '  \n')}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
